import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import PreferencesCheckbox from "components/ProjectSpecificComponents/Elements/Global/atoms/PreferencesCheckbox"
import Select from "components/ProjectSpecificComponents/Elements/Global/atoms/Select"
import { INewRequestFormField } from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import {
	isReactSelectOptionArray,
	isReactSelectSingleOption,
	TOnChangeReactSelectField,
	TReactSelectOption,
	TUserOption,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import MainFieldSelect from "../../atoms/MainFieldSelect"
import FormField, { TFormFieldDirection } from "../FormField"
import filterUsedFieldsWithArrays from "./filterUsedFieldWithArrays"
import ToolFieldPropertyIOTLevels from "./ToolFieldProperty.IOT.Levels"
import { observer } from "mobx-react-lite"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"

interface Props {
	picklistId?: string | null
	pickListOptions?: TReactSelectOption[]
	itemOfTypeOptions?: TReactSelectOption[]
	itemOfTypeBooleanOptions?: TReactSelectOption[]
	itemOfTypeUserOptions?: TReactSelectOption[]
	predicateFieldKeysOptions: TReactSelectOption[]
	predicateFieldValuesOptions?: TReactSelectOption[]
	isFieldUnconditionallyRequired: boolean
	value: INewRequestFormField
	/**
	 * Not implemented yet
	 */
	editable?: boolean
	onChangeFormData?: (data: Partial<INewRequestFormField>) => void
	picklistsForItem: TReactSelectOption[]
	isUserSelected?: boolean
}

/**
 * Form component for one tool field's configuration
 */
const ToolFieldProperties = observer(
	({
		picklistId,
		pickListOptions,
		itemOfTypeOptions,
		itemOfTypeBooleanOptions,
		itemOfTypeUserOptions,
		predicateFieldKeysOptions,
		predicateFieldValuesOptions,
		isFieldUnconditionallyRequired = false,
		value: {
			charLimit,
			fieldType,
			isConditional,
			isMultilineField,
			isRequired,
			excludedItemOfTypeValues,
			mainFieldKey,
			mainFieldLabel,
			nameLV,
			nameEN,
			excludedPickListItemValues,
			predicateFieldKey,
			predicateValues,
			pickListValues,
			itemOfTypeBooleanFieldId,
			itemOfTypeUserFieldId,
			itemOfTypeBooleanItemId,
			itemOfTypeLevels,
			itemOfTypeId,
		},
		editable,
		onChangeFormData = () => {},
		picklistsForItem,
		isUserSelected = false,
	}: Props) => {
		const { isMobile } = useScreenType()
		const { t } = useTranslation()

		const formFieldDirection: TFormFieldDirection = isMobile ? "Horizontal" : "Vertical"
		const formSectionContainerStyle: React.CSSProperties = isMobile
			? {}
			: {
					maxWidth: "240px",
			  }

		// If field is pick list then add one more property to store pickListValues
		// pickListValues contains avaible pick list options for user side
		useEffect(() => {
			if (!pickListOptions || !excludedPickListItemValues) return

			if (fieldType == "PICKLIST" || fieldType == "MULTI_PICKLIST") {
				const picklistValues = filterUsedFieldsWithArrays(
					pickListOptions,
					excludedPickListItemValues,
					"value",
					"value"
				)

				onChangeFormData({ pickListValues: picklistValues, picklistId: picklistId })
			}
		}, [excludedPickListItemValues])

		// When main field is changed with diffrent type updates store with new fieldType
		// Cant be used in onChange in mainFieldSelect
		// Because new fieldType value is recieved only after changing mainFieldKey
		useEffect(() => {
			onChangeFormData({
				fieldType: fieldType,
			})
		}, [fieldType])

		useEffect(() => {
			onChangeFormData({
				itemOfTypeLevels: {
					...itemOfTypeLevels,
					itemOfTypeId: itemOfTypeId,
				},
			})
		}, [itemOfTypeId])

		const onItemOfTypeChange: TOnChangeReactSelectField = (
			selectedOption,
			triggeredAction,
			fieldType?: TUserOption
		) => {
			if (triggeredAction?.action === "clear") {
				switch (fieldType) {
					case "bool":
						onChangeFormData({
							itemOfTypeBooleanFieldId: "",
							itemOfTypeBooleanItemId: "",
						})
						break
					case "userValue":
						onChangeFormData({
							itemOfTypeUserFieldId: "",
							itemOfTypeUserItemId: "",
						})
						break
				}
				return
			}

			if (!isReactSelectSingleOption(selectedOption)) return

			switch (fieldType) {
				case "bool":
					onChangeFormData({
						itemOfTypeBooleanFieldId: selectedOption.value,
						itemOfTypeBooleanItemId: selectedOption.extraProp,
					})
					break
				case "userValue":
					onChangeFormData({
						itemOfTypeUserFieldId: selectedOption.value,
						itemOfTypeUserItemId: selectedOption.extraProp,
					})
					break
			}
		}

		return (
			<HyperDiv id="tool-field-property-container" direction="column">
				<HyperDiv
					id="tool-field-row-or-column"
					direction={isMobile ? "column" : "row"}
					className="w-100-children"
					alignedTop
					stretchedAcross
				>
					<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
						<FormField
							direction={formFieldDirection}
							label={t("forms.field-label.field")}
							placeholderValue=""
							editable
						>
							<MainFieldSelect
								valueKey={mainFieldKey}
								isDisabled={isFieldUnconditionallyRequired}
								onChange={(selectedOption) => {
									onChangeFormData(
										isReactSelectSingleOption(selectedOption)
											? {
													mainFieldKey: selectedOption.value,
													mainFieldLabel: selectedOption.label,
											  }
											: {
													mainFieldKey: "",
													mainFieldLabel: "",
											  }
									)
								}}
								type={"newRequestForm"}
							/>
						</FormField>
					</HyperDiv>

					{mainFieldKey != null && (
						<>
							{pickListOptions && (
								<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
									<FormField
										direction={formFieldDirection}
										label={t("forms.field-label.picklist")}
										placeholderValue=""
										editable
									>
										<Select
											value={excludedPickListItemValues}
											options={pickListOptions}
											isMulti
											onChange={(selectedOptions) => {
												if (!isReactSelectOptionArray(selectedOptions)) return
												onChangeFormData({ excludedPickListItemValues: [...selectedOptions] })
											}}
										/>
									</FormField>
								</HyperDiv>
							)}
							<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
								<FormField
									direction={formFieldDirection}
									label={t("forms.field-label.type")}
									editable
									readOnly
									placeholderValue={fieldType}
								/>
							</HyperDiv>
							<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
								<FormField
									direction={formFieldDirection}
									label={t("forms.field-label.required")}
									placeholderValue=""
									editable
								>
									<PreferencesCheckbox
										checked={isFieldUnconditionallyRequired || isRequired}
										readOnly={isFieldUnconditionallyRequired}
										onCheck={(checked) => onChangeFormData({ isRequired: checked })}
									/>
								</FormField>
							</HyperDiv>
							{fieldType == "STRING" && (
								<>
									<HyperDiv padding="0px 10px " style={formSectionContainerStyle}>
										<FormField
											direction={formFieldDirection}
											label={t("forms.field-label.string-multi")}
											placeholderValue=""
											editable
										>
											<PreferencesCheckbox
												checked={isMultilineField}
												onCheck={(checked) => onChangeFormData({ isMultilineField: checked })}
											/>
										</FormField>
									</HyperDiv>
									<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
										<FormField
											direction={formFieldDirection}
											label={t("forms.field-label.string-char")}
											placeholderValue=""
											value={charLimit}
											editable
											onChange={(e) => onChangeFormData({ charLimit: e.target.value })}
										/>
									</HyperDiv>
								</>
							)}
							{itemOfTypeOptions && (
								<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
									<FormField
										direction={formFieldDirection}
										label={t("forms.field-label.item-of-type-condition")}
										placeholderValue=""
										editable
									>
										<Select
											valueKey={itemOfTypeBooleanFieldId}
											options={itemOfTypeBooleanOptions}
											onChange={(selectedOption, triggeredAction) => {
												onItemOfTypeChange(selectedOption, triggeredAction, "bool")
											}}
										/>
									</FormField>
								</HyperDiv>
							)}
							<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
								<FormField
									direction={formFieldDirection}
									label={t("forms.field-label.conditionally")}
									placeholderValue=""
									editable
								>
									<PreferencesCheckbox
										checked={isConditional}
										onCheck={(checked) => {
											onChangeFormData({ isConditional: checked })
										}}
									/>
								</FormField>
							</HyperDiv>
							{itemOfTypeOptions && (
								<>
									<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
										<FormField
											direction={formFieldDirection}
											label={t(`forms.field-label.responsible-field`)}
											placeholderValue=""
											editable
										>
											<Select
												isDisabled={!!itemOfTypeUserFieldId ? false : isUserSelected}
												valueKey={itemOfTypeUserFieldId}
												options={itemOfTypeUserOptions}
												onChange={(selectedOption, triggeredAction) => {
													onItemOfTypeChange(selectedOption, triggeredAction, "userValue")
												}}
											/>
										</FormField>
									</HyperDiv>
								</>
							)}
						</>
					)}
				</HyperDiv>

				{isMobile ? <Whitespace x="1" /> : <Whitespace x="2" />}

				{itemOfTypeOptions && (
					<>
						<ToolFieldPropertyIOTLevels
							firstLevel={true}
							itemOfTypeLevels={itemOfTypeLevels}
							onChangeFormData={onChangeFormData}
							picklistsForItem={picklistsForItem}
						/>

						{itemOfTypeLevels.levelOneEnabled && (
							<ToolFieldPropertyIOTLevels
								firstLevel={false}
								itemOfTypeLevels={itemOfTypeLevels}
								onChangeFormData={onChangeFormData}
								picklistsForItem={picklistsForItem}
							/>
						)}
					</>
				)}
				{isConditional && (
					<HyperDiv
						id="field-conditional-cnfg-section"
						direction={isMobile ? "column" : "row"}
						className="w-100-children"
						centeredY
						padding="10px 0px"
						style={{ background: "var(--light-light-gray)" }}
					>
						<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
							<FormField
								direction={formFieldDirection}
								label={t("forms.field-label.field")}
								placeholderValue=""
								editable
							>
								<Select
									valueKey={predicateFieldKey}
									options={predicateFieldKeysOptions}
									onChange={(selectedOption) => {
										onChangeFormData({
											predicateFieldKey: isReactSelectSingleOption(selectedOption)
												? selectedOption.value
												: undefined,
											predicateValues: [],
										})
									}}
								/>
							</FormField>
						</HyperDiv>

						<HyperDiv padding="0px 10px" style={{ width: "100%" }}>
							<FormField
								direction={formFieldDirection}
								label={t("forms.field-label.values")}
								placeholderValue=""
								editable
							>
								<Select
									value={predicateValues}
									options={predicateFieldValuesOptions}
									isMulti
									onChange={(selectedOptions) => {
										if (!isReactSelectOptionArray(selectedOptions)) return
										onChangeFormData({ predicateValues: [...selectedOptions] })
									}}
								/>
							</FormField>
						</HyperDiv>
					</HyperDiv>
				)}
			</HyperDiv>
		)
	}
)

export default ToolFieldProperties
